import { CommonModule, LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Output,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatCardContent } from '@angular/material/card';
import { MatChip } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy } from '@core/classes/untilDestroy.class';
import { NOTICE_TYPE } from '@core/constants';
import { TimeAgoPipe } from '@core/pipes/time-ago.pipe';
import { TypeOfPipe } from '@core/pipes/type-of.pipe';
import { UaParserPipe } from '@core/pipes/ua-parser.pipe';
import { isMobile } from '@core/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, take, takeUntil } from 'rxjs';
import { BankInfoComponent } from '../bank-info/bank-info.component';
import { PbbCardComponent } from '../pbb-card/pbb-card.component';
import { PbbNoticeComponent } from '../pbb-notice/pbb-notice.component';

export interface MenuAction {
  action: string;
  item: any;
  itemRef?: string;
  itemIndex?: number;
}

export interface MenuOption {
  action: string;
  label: string;
  icon: string;
  logo?: string;
  color?: string;
  showConfirm: boolean;
  showLogo?: boolean;
  imageSrc?: string;
  confirmTitle?: string | Array<any> | ((item: any, index: number) => string);
  confirmSubtitle?: string | Array<any> | ((item: any, index: number) => string);
  confirmColor?: string;
  confirmButton?: string | Array<any>;
  confirmCancel?: string | Array<any>;
  confirmCancelColor?: string;
  disabled?: ((item: any, index: number) => boolean) | boolean;
  clicked?: (item: any, index: number) => void;
  closeOnSuccess?: boolean;
  dialogSuccessMessageParams?: any;
  dialogErrorMessageParams?: any;
  deleteTrusted?: boolean;
}

const getTranslation = (
  option: any,
  property: string,
  translateService: TranslateService,
  item?: any,
  itemIndex?: number
): string => {
  const translateObject = option[property];
  if (typeof translateObject === 'string') {
    return translateService.instant(translateObject);
  } else if (Array.isArray(option[property])) {
    return translateService.instant(translateObject[0], translateObject[1]);
  } else if (typeof translateObject === 'function') {
    return translateObject(item, itemIndex);
  }
  return '';
};

@Component({
  selector: 'pbb-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatMenuModule, MatIconModule, TypeOfPipe, TranslateModule, LowerCasePipe, CommonModule],
})
export class MenuComponent extends UntilDestroy implements OnDestroy {
  @Input() item!: any;
  @Input() itemRef?: string;
  @Input() itemIndex!: number;
  @Input() options: Array<MenuOption> = [];

  @Output() menuAction: EventEmitter<MenuAction> = new EventEmitter();

  isMobile = isMobile();
  @Input() menuAriaLabel?: string = '';
  @Input() menuTitleKey?: string = '';
  @Input() reverseButtonIcon?: boolean = false;
  @Input() openDesktopDialog?: boolean = false;
  @Input() idLabel!: string;
  @Input() disabled = false;
  dialogMessageKey!: string;
  isSuccess = false;
  constructor(private readonly _bottomSheet: MatBottomSheet, public dialog: MatDialog) {
    super();
  }

  openDesktopDialogFn(): void {
    const dialogRef = this.dialog.open(MenuMobileComponent, {
      data: {
        options: this.options,
        item: this.item,
        itemRef: this.itemRef,
        index: this.itemIndex,
        menuTitleKey: this.menuTitleKey,
        reverseButtonIcon: this.reverseButtonIcon,
      },
      panelClass: ['content_overlay-medium', 'block'],
      autoFocus: false,
      backdropClass: 'content_overlay-dialog',
    });    

    dialogRef.componentInstance.onConfirm$.pipe(takeUntil(this.destroy$)).subscribe((option) => {
      this.menuAction.emit({
        action: option.action,
        item: this.item,
        itemIndex: this.itemIndex,
        itemRef: this.itemRef,
      });
      if (!option.showConfirm) {
        dialogRef.close();
      }
    });
  }

  openMobileMenu(): void {
    const btmSheetRef = this._bottomSheet.open(MenuMobileComponent, {
      data: {
        options: this.options,
        item: this.item,
        itemRef: this.itemRef,
        index: this.itemIndex,
        menuTitleKey: this.menuTitleKey,
        reverseButtonIcon: this.reverseButtonIcon,
      },
      panelClass: 'content_main-drawer',
      autoFocus: false,
      backdropClass: 'content_main-bottom-sheet',
    });

    btmSheetRef.instance.onConfirm$.pipe(takeUntil(this.destroy$)).subscribe((option) => {
      this.menuAction.emit({
        action: option.action,
        item: this.item,
        itemIndex: this.itemIndex,
        itemRef: this.itemRef,
      });
      if (!option.showConfirm) {
        btmSheetRef.dismiss();
      }
    });
  }

  desktopOptionClicked(option: MenuOption): void {
    if (option.clicked) {
      option.clicked(this.item, this.itemIndex);
    }
    this.menuAction.emit({
      action: option.action,
      item: this.item,
      itemIndex: this.itemIndex,
      itemRef: this.itemRef,
    });
  }

  openDesktopMenu(option: MenuOption): void {
    if (option.showConfirm) {
      const dialogRef = this.dialog.open(MenuDesktopConfirmComponent, {
        data: {
          option: option,
          item: this.item,
          itemRef: this.itemRef,
          index: this.itemIndex,
        },
        closeOnNavigation: true,
        autoFocus: false,
        width: '416px',
        panelClass: 'content_overlay-medium',
        backdropClass: 'content_overlay-dialog',
      });
      dialogRef.componentInstance.onConfirm$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.menuAction.emit({
          action: option.action,
          item: this.item,
          itemIndex: this.itemIndex,
          itemRef: this.itemRef,
        });
      });
    } else {
      this.desktopOptionClicked(option);
    }
  }
}

@Component({
  selector: 'pbb-menu-mobile',
  templateUrl: './menu-mobile.component.html',
  styleUrls: ['./menu.component.scss'],
  imports: [
    MatMenuModule,
    MatIconModule,
    PbbNoticeComponent,
    MatButtonModule,
    TypeOfPipe,
    TranslateModule,
    LowerCasePipe,
    PbbCardComponent,
    MatCardContent,
    MatChip,
    TimeAgoPipe,
    UaParserPipe,
    BankInfoComponent,
    CommonModule
],
})
export class MenuMobileComponent implements OnDestroy {
  options: Array<MenuOption> = [];
  selectedOption!: MenuOption;
  item: any;
  itemIndex: number;
  itemRef: string;
  currentDisplay = 'menu';
  Array = Array;
  notice = NOTICE_TYPE;
  onConfirm$ = new EventEmitter<MenuOption>();
  onClose$ = new EventEmitter<boolean>();
  dialogMessageKey!: string;
  dialogMessageSubs!: Subscription;
  isSuccess: boolean = false;
  isErrorInline = true;
  errorKey: string = '';
  menuTitleKey!: string;
  reverseButtonIcon: boolean = false;
  constructor(
    private readonly translateService: TranslateService,
    @Optional() private readonly _bottomSheetRef: MatBottomSheetRef<MenuMobileComponent>,
    @Optional() private readonly _dialogRef: MatDialogRef<MenuMobileComponent>,
    private readonly cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) private readonly data: any,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly dialogData: any
  ) {
    const tempData = this.data || this.dialogData || {};

    this.options = tempData.options;
    this.item = tempData.item;
    this.itemIndex = tempData.index;
    this.itemRef = tempData.itemRef;
    this.isErrorInline = tempData.isErrorInline ?? true;
    this.errorKey = tempData.errorKey ?? '';
    this.currentDisplay = tempData.currentDisplay ?? 'menu';
    this.menuTitleKey = tempData.menuTitleKey;
    this.reverseButtonIcon = tempData.reverseButtonIcon ?? false;
  }

  ngOnDestroy(): void {
    this.dialogMessageSubs?.unsubscribe();
  }
  getTranslation(option: any, property: string): string {
    return getTranslation(option, property, this.translateService, this.item, this.itemIndex);
  }

  backToMenu(): void {
    this.currentDisplay = 'menu';
    if(this._bottomSheetRef) this._bottomSheetRef.dismiss();
    if(this._dialogRef) this._dialogRef.close();
  }

  confirm(option: MenuOption): void {
    if (option.clicked) {
      option.clicked(this.item, this.itemIndex);
    }
    this.selectedOption = option;
    if (option.closeOnSuccess) {
      if(this._bottomSheetRef) this._bottomSheetRef.dismiss();
      if(this._dialogRef) this._dialogRef.close();
    }
    this.onConfirm$.emit(option);
  }

  click(option: MenuOption): void {
    if (option.showConfirm) {
      this.currentDisplay = option.action;
    } else {
      this.confirm(option);
    }
  }
}

@Component({
  selector: 'pbb-menu-desktop-confirm',
  templateUrl: './menu-desktop-confirm.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    PbbNoticeComponent,
    TranslateModule,
    LowerCasePipe,
    PbbCardComponent,
    MatCardContent,
    MatChip,
    TimeAgoPipe,
    UaParserPipe,
    BankInfoComponent,
  ],
})
export class MenuDesktopConfirmComponent implements OnDestroy {
  option: MenuOption;
  item: any;
  itemIndex: number;
  itemRef: string;
  dialogMessageKey!: string;
  Array = Array;
  notice = NOTICE_TYPE;
  onConfirm$ = new EventEmitter<MenuOption>();
  onClose$ = new EventEmitter<boolean>();
  dialogMessageSubs!: Subscription;
  isSuccess: boolean = false;
  loading: boolean = false;
  isErrorInline = true;
  constructor(
    private readonly translateService: TranslateService,
    public dialogRef: MatDialogRef<MenuDesktopConfirmComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.option = data.option;
    this.item = data.item;
    this.itemIndex = data.itemIndex;
    this.itemRef = data.itemRef;
    this.isErrorInline = data.isErrorInline ?? true;
  }
  ngOnDestroy(): void {
    this.dialogMessageSubs?.unsubscribe();
  }

  getTranslation(option: any, property: string): string {
    return getTranslation(option, property, this.translateService, this.item, this.itemIndex);
  }

  click(): void {
    if (this.option.clicked) {
      this.option.clicked(this.item, this.itemIndex);
    }
  }

  confirm(action: MenuOption) {
    if (this.option.closeOnSuccess) {
      this.dialogRef.close();
    }
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        this.onConfirm$.emit(action);
      });
  }
}
